export const DEPLOYMENT_STATUSES = [
  "pending",
  "starting",
  "building",
  "deploying",
  "network",
  "finalizing",
  "ready",
  "failed",
  "skipped",
  "awaiting_approval",
  "stopped",
  "superseded",
  "cancelled",
] as const;

export type DeploymentStatus = (typeof DEPLOYMENT_STATUSES)[number];

export const DEPLOYMENT_STATUS_LABELS: Record<DeploymentStatus, string> = {
  pending: "Pending",
  starting: "Starting",
  building: "Building",
  deploying: "Deploying",
  network: "Assigning Domains",
  finalizing: "Finalizing",
  ready: "Ready",
  failed: "Failed",
  skipped: "Skipped",
  awaiting_approval: "Awaiting Approval",
  stopped: "Stopped",
  superseded: "Superseded",
  cancelled: "Cancelled",
};

// Statuses where the deployment has settled and won't change on its own. A
// deployment in any other status is still progressing, so consumers poll for
// updates while one is in flight.
const TERMINAL_DEPLOYMENT_STATUSES = new Set<DeploymentStatus>([
  "ready",
  "failed",
  "skipped",
  "awaiting_approval",
  "stopped",
  "superseded",
  "cancelled",
]);

export function isDeploymentInFlight(status: DeploymentStatus): boolean {
  return !TERMINAL_DEPLOYMENT_STATUSES.has(status);
}

// The filter groups a user picks from. The pipeline's intermediate statuses
// (starting, network, finalizing, ...) read as one "building" phase from the
// outside. Every raw status belongs to exactly one group; the test enforces it.
export const DEPLOYMENT_STATUS_GROUPS = {
  ready: ["ready"],
  failed: ["failed"],
  building: ["starting", "building", "deploying", "network", "finalizing"],
  queued: ["pending"],
  blocked: ["awaiting_approval"],
  cancelled: ["cancelled"],
  skipped: ["skipped"],
  superseded: ["superseded"],
  stopped: ["stopped"],
} as const satisfies Record<string, readonly DeploymentStatus[]>;

export type DeploymentStatusGroup = keyof typeof DEPLOYMENT_STATUS_GROUPS;

export const DEPLOYMENT_STATUS_GROUP_NAMES = Object.keys(
  DEPLOYMENT_STATUS_GROUPS,
) as DeploymentStatusGroup[];

const GROUP_BY_STATUS = new Map<DeploymentStatus, DeploymentStatusGroup>(
  DEPLOYMENT_STATUS_GROUP_NAMES.flatMap((group) =>
    DEPLOYMENT_STATUS_GROUPS[group].map((status) => [status, group] as const),
  ),
);

export const DEPLOYMENT_GROUP_COLOR: Record<DeploymentStatusGroup, string> = {
  ready: "bg-success-9",
  failed: "bg-error-9",
  building: "bg-signal",
  queued: "bg-gray-9",
  blocked: "bg-warning-9",
  cancelled: "bg-gray-9",
  skipped: "bg-gray-9",
  superseded: "bg-gray-9",
  stopped: "bg-gray-9",
};

export function statusGroupOf(status: DeploymentStatus): DeploymentStatusGroup {
  const group = GROUP_BY_STATUS.get(status);
  if (!group) {
    throw new Error(`Deployment status ${status} belongs to no group`);
  }
  return group;
}

export function deploymentStatusColor(status: DeploymentStatus): string {
  return DEPLOYMENT_GROUP_COLOR[statusGroupOf(status)];
}

// Groups holding deployments that are out of play. They accumulate on an active
// app and bury the rows a user came to look at, so the list starts with them
// filtered out; selecting them in the status filter brings them back.
const DEPLOYMENT_STATUS_GROUPS_HIDDEN_BY_DEFAULT = new Set<DeploymentStatusGroup>([
  "cancelled",
  "skipped",
  "superseded",
  "stopped",
]);

export const DEFAULT_DEPLOYMENT_STATUS_GROUPS = DEPLOYMENT_STATUS_GROUP_NAMES.filter(
  (group) => !DEPLOYMENT_STATUS_GROUPS_HIDDEN_BY_DEFAULT.has(group),
);

export function isDeploymentStatusGroup(value: string): value is DeploymentStatusGroup {
  return Object.hasOwn(DEPLOYMENT_STATUS_GROUPS, value);
}

export function expandDeploymentStatusGroups(
  groups: readonly DeploymentStatusGroup[],
): DeploymentStatus[] {
  return groups.flatMap((group) => DEPLOYMENT_STATUS_GROUPS[group]);
}

// Stop and wake record the desired state first and flip the status only once the
// instances have scaled, so a mismatch between the two means the row is still moving.
export function isDeploymentSettling(deployment: {
  status: DeploymentStatus;
  desiredState: "running" | "stopped";
}): boolean {
  if (isDeploymentInFlight(deployment.status)) {
    return true;
  }
  return (
    (deployment.status === "ready" && deployment.desiredState === "stopped") ||
    (deployment.status === "stopped" && deployment.desiredState === "running")
  );
}
